"use client";

import { createContext, useContext, useState, useTransition, type FormEvent, type ReactNode } from "react";
import { useFormStatus } from "react-dom";
import { useRouter } from "next/navigation";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import type { ActionResult } from "@/lib/actions";
import { useT } from "@/components/i18n-ui/provider";

/** Pending state for ActionForm (which submits via a transition, so useFormStatus can't see it). */
const PendingCtx = createContext(false);

export function SubmitButton({ children, pendingText, className = "btn-primary", disabled, name, value }: { children: ReactNode; pendingText?: string; className?: string; disabled?: boolean; name?: string; value?: string }) {
  const status = useFormStatus();
  const pending = useContext(PendingCtx) || status.pending;
  return (
    <button type="submit" className={className} disabled={pending || disabled} name={name} value={value} aria-busy={pending}>
      {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {pending && pendingText ? pendingText : children}
    </button>
  );
}

type Action = (prev: ActionResult | null, fd: FormData) => Promise<ActionResult>;

/**
 * Form wired to a server action. Submits through a transition instead of `<form action>`
 * because React 19 resets uncontrolled fields after every action submission — which would
 * wipe what the user typed when the server returns a validation error. Server-side
 * redirects from the action still navigate normally.
 */
export function ActionForm({
  action,
  children,
  className = "",
  refresh = true,
  successRedirect,
  resetOnSuccess,
  showSuccess = true,
  onSuccess,
}: {
  action: Action;
  children: ReactNode;
  className?: string;
  refresh?: boolean;
  successRedirect?: string | ((data: unknown) => string | undefined);
  resetOnSuccess?: boolean;
  showSuccess?: boolean;
  onSuccess?: (data: unknown) => void;
}) {
  const [state, setState] = useState<ActionResult | null>(null);
  const [pending, start] = useTransition();
  const router = useRouter();
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const form = e.currentTarget;
    const fd = new FormData(form, (e.nativeEvent as SubmitEvent).submitter ?? undefined);
    start(async () => {
      const r = await action(state, fd);
      setState(r);
      if (!r?.ok) return;
      if (resetOnSuccess) form.reset();
      onSuccess?.(r.data);
      const to = typeof successRedirect === "function" ? successRedirect(r.data) : successRedirect;
      if (to) router.push(to);
      else if (refresh) router.refresh();
    });
  }
  return (
    <PendingCtx.Provider value={pending}>
      <form onSubmit={submit} className={className} aria-busy={pending}>
        {children}
        <FormMessage state={state} showSuccess={showSuccess} />
      </form>
    </PendingCtx.Provider>
  );
}

export function FormMessage({ state, showSuccess = true }: { state: ActionResult | null; showSuccess?: boolean }) {
  const t = useT("ui");
  if (!state) return null;
  if (state.ok) {
    if (!showSuccess) return null;
    return (
      <p role="status" className="mt-3 flex items-center gap-1.5 text-sm text-emerald-700">
        <CheckCircle2 className="h-4 w-4" aria-hidden /> {state.message ?? t("saved")}
      </p>
    );
  }
  return (
    <p role="alert" className="mt-3 flex items-start gap-1.5 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {state.error}
    </p>
  );
}

export function Field({ label, name, hint, children, className = "" }: { label: string; name?: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="label" htmlFor={name}>{label}</label>
      {children}
      {hint && <p className="hint">{hint}</p>}
    </div>
  );
}
